import Api from "../../../services/api";
import { TMultiCallParam } from "../../../utils/api.interfaces";
import { handleApi } from "../../../utils/error";

/**
 * Resolves rule names for a set of rule IDs via a single MultiCall.
 *
 * Returns an empty `Map` on any API error (graceful degradation via `handleApi`)
 * so a transient rule lookup failure does not collapse the caller's whole result.
 *
 * @param ruleIds - Rule IDs to resolve. Duplicates and empty strings are tolerated.
 * @param api - MyGeotab API instance.
 * @returns Map from rule ID to rule name. Unresolvable IDs are absent from the map.
 */
export async function fetchRuleNames(
    ruleIds: readonly string[],
    api: Api
): Promise<Map<string, string>> {
    const uniqueIds = new Set<string>();
    for (let i = 0; i < ruleIds.length; i++) {
        const id = ruleIds[i];
        if (id) {
            uniqueIds.add(id);
        }
    }

    if (uniqueIds.size === 0) {
        return new Map();
    }

    try {
        const calls: TMultiCallParam[] = Array.from(uniqueIds).map((id) => [
            "Get",
            { typeName: "Rule", search: { id } }
        ]);
        const results: unknown[][] = await api.multiCallPromise(calls);

        const map = new Map<string, string>();
        for (const result of results) {
            const rule = Array.isArray(result)
                ? (result[0] as { id?: string; name?: string })
                : undefined;
            if (rule?.id && rule?.name) {
                map.set(rule.id, rule.name);
            }
        }
        return map;
    } catch (err) {
        handleApi(err);
        return new Map();
    }
}
